import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import type {ApexOptions} from 'apexcharts'
import {ApexChart, useChartTokens} from '@/components/ui/apex-chart'
import {SectionCard} from '@/components/section-card'
import type {DemoCorte} from '@/hooks/bateria/use-bateria-informe'
import {MAX_CATEGORIAS_VERTICAL} from '@/lib/bateria'

function soloEnteros(value: string | number): string {
  const n = Number(value)
  return Number.isInteger(n) ? String(n) : ''
}

/** Barras de porcentaje (el conteo va en el tooltip). El umbral de horizontal replica
 * `bateria_reporte_charts._MAX_CATEGORIAS_VERTICAL` para que pantalla y Word coincidan. */
function CorteChart({corte}: {corte: DemoCorte}) {
  const {t} = useTranslation()
  const tokens = useChartTokens()
  const total = corte.pares.reduce((acc, [, n]) => acc + n, 0)
  const horizontal = corte.pares.length >= MAX_CATEGORIAS_VERTICAL
  const pcts = useMemo(
    () => corte.pares.map(([, n]) => (total === 0 ? 0 : Math.round((n / total) * 1000) / 10)),
    [corte, total]
  )
  const options = useMemo<ApexOptions>(
    () => ({
      chart: {type: 'bar'},
      colors: [tokens.primary],
      plotOptions: {
        bar: {horizontal, borderRadius: 3, borderRadiusApplication: 'end', barHeight: '58%', columnWidth: '58%'},
      },
      xaxis: horizontal
        ? {categories: corte.pares.map(([label]) => label), labels: {formatter: (v: string) => `${v}%`}, max: 100}
        : {categories: corte.pares.map(([label]) => label)},
      yaxis: horizontal ? {} : {labels: {formatter: (v: number) => `${v}%`}, max: 100},
      grid: {xaxis: {lines: {show: horizontal}}, yaxis: {lines: {show: !horizontal}}},
      tooltip: {
        y: {
          formatter: (_value: number, opts?: {dataPointIndex: number}) => {
            const [, n] = corte.pares[opts?.dataPointIndex ?? 0]
            return t('admin.bateria.informe.sociodemografico.tooltip', {count: n, pct: pcts[opts?.dataPointIndex ?? 0]})
          },
        },
      },
      dataLabels: {enabled: !horizontal, formatter: soloEnteros, style: {fontSize: '10px'}},
    }),
    [horizontal, corte, tokens, pcts, t]
  )
  const series = useMemo(() => [{name: corte.titulo, data: pcts}], [corte, pcts])

  return (
    <div data-testid={`informe-corte-${corte.titulo}`}>
      <p className="text-sm font-medium text-foreground">{corte.titulo}</p>
      <div className={horizontal ? 'mt-2' : 'mt-2 overflow-x-auto'}>
        <ApexChart
          type="bar"
          height={horizontal ? Math.max(160, corte.pares.length * 32) : 220}
          className={horizontal ? undefined : 'min-w-[320px]'}
          options={options}
          series={series}
        />
      </div>
    </div>
  )
}

/** Bloque 6 (§P7): el servidor ya aplica el umbral de anonimato del art. 5. */
export function CortesSociodemograficos({cortes}: {cortes: DemoCorte[]}) {
  const {t} = useTranslation()
  if (cortes.length === 0) return null
  return (
    <SectionCard
      title={t('admin.bateria.informe.sociodemografico.title')}
      subtitle={t('admin.bateria.informe.sociodemografico.subtitle')}
      testId="informe-sociodemografico">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {cortes.map((corte) => (
          <CorteChart key={corte.titulo} corte={corte} />
        ))}
      </div>
    </SectionCard>
  )
}
